import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RefreshCw, AlertTriangle, Save, Check, Search, Filter, X, Plus, Upload, FileDown, RotateCcw, Trash2, Clock } from 'lucide-react';
import WorkTimelineModal from './WorkTimelineModal';
import { worksApiService, FullFormatRow } from '../../services/workApi.service';

// Public hierarchy master-data endpoints (same pattern as WorksList filters).
const API_BASE = process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1';

// Shape returned by the import dry-run (/works-excel-import/validate).
type ImportPreview = {
  summary: { total: number; valid: number; invalid: number; duplicates: number };
  invalid?: Array<{ row: number; errors: string[] }>;
  message?: string;
};
const unwrapList = (j: any): any[] => (Array.isArray(j) ? j : j?.data ?? j?.items ?? []);
async function fetchHierarchy(path: string): Promise<any[]> {
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) return [];
    return unwrapList(await res.json());
  } catch {
    return [];
  }
}

// ==========================================
// 38-COLUMN "WW FORMAT" EDITABLE GRID
// ------------------------------------------
// Mirrors the works-upload spreadsheet: every column shown, raw inputs are
// editable, the four formula columns recalculate live as you type, hierarchy
// names are read-only, and each row saves back via PUT /works/:id/full-format.
// ==========================================

type CellKind = 'num' | 'text' | 'date' | 'select' | 'depselect' | 'readonly' | 'formula' | 'derived';

interface ColumnDef {
  col: number; // 1-based column number from the WW Format template
  key: keyof FullFormatRow | string;
  label: string;
  group: string;
  kind: CellKind;
  width?: number;
  options?: string[];
  // For formula columns: derive the value from the (live) row being edited.
  compute?: (r: EditableRow) => number;
  // For 'derived' (read-only) columns: render a display string from the row.
  render?: (r: EditableRow) => { text: string; cls?: string };
  // For dependent dropdowns ('depselect'): the parent field this cascades from,
  // and the options for a given parent value.
  dependsOn?: string;
  depOptions?: (parentValue: any) => string[];
  // Row-aware variant: when options depend on MORE than one field (e.g. Sub Stage
  // depends on Abstract Stage + Type of work). `dependsOnKeys` lists every field
  // that should trigger a re-validation of this dropdown.
  dependsOnKeys?: string[];
  depOptionsRow?: (row: EditableRow) => string[];
}

// Transient per-row UI state lives alongside the data.
type EditableRow = FullFormatRow & {
  _dirty?: boolean;
  _saving?: boolean;
  _saved?: boolean;
  _error?: string | null;
};

const num = (v: any): number => {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

// The four derived columns (Balance spilled & Cumulative, for length & cost).
const fBalanceLength = (r: EditableRow) => num(r.length_sanctioned_km) - num(r.fy_length_upto_last_fy);
const fCumulativeLength = (r: EditableRow) => num(r.fy_length_upto_last_fy) + num(r.fy_length_during);
const fBalanceCost = (r: EditableRow) => num(r.estimate_cost) - num(r.fy_exp_upto_last_fy);
const fCumulativeExp = (r: EditableRow) => num(r.fy_exp_upto_last_fy) + num(r.fy_exp_during);

// Read-only monitoring renderers (values are derived server-side).
const fEoat = (r: EditableRow): { text: string; cls?: string } =>
  num(r.is_eoat)
    ? { text: `Yes (${num(r.eoat_days)}d)`, cls: 'text-red-700 font-semibold' }
    : { text: 'No', cls: 'text-gray-400' };
const fQcStatus = (r: EditableRow): { text: string; cls?: string } => {
  const s = String(r.qc_status ?? '').trim();
  if (s === 'Inspected') return { text: s, cls: 'text-green-700 font-medium' };
  if (s === 'To be inspected') return { text: s, cls: 'text-amber-700 font-medium' };
  return { text: '-', cls: 'text-gray-400' };
};
const fQcWing = (r: EditableRow): { text: string; cls?: string } => ({ text: String(r.qc_wing ?? '').trim() || '-' });
const fQcGrade = (r: EditableRow): { text: string; cls?: string } => ({ text: String(r.qc_grade ?? '').trim() || '-' });

const GRANT_LEVELS = ['State', 'Central'];
const ABSTRACT_STAGES = ['Not Started', 'In Progress', 'Completed', 'Held Up'];

// Sub Stage (col 26) is a DEPENDENT dropdown driven by Abstract Stage (col 25)
// AND — when In Progress — the kind of structure (Type of work / Sub Type). This
// mirrors the official upload-format "Data Base" matrix and the backend
// work-substages.ts, so a selection always passes the server's
// stageConsistencyError check.
type StructureGroup = 'ROAD' | 'CC_ROAD' | 'CULVERT' | 'BRIDGE' | 'BUILDING';
const IN_PROGRESS_STAGES_BY_GROUP: Record<StructureGroup, string[]> = {
  ROAD:     ['Formation', 'CD works', 'GSB Stage', 'WMM Stage', 'BT/ CC Stage', 'Finishings'],
  CC_ROAD:  ['Preparation', 'CC Laid'],
  CULVERT:  ['Foundation', 'Sub Structure', 'Slab/Pipe/Box Casting', 'Wing Walls', 'Approaches', 'Finishings'],
  BRIDGE:   ['Foundation', 'Footing Stage', 'Sub Structure', 'Super Structure', 'Approaches', 'Finishings'],
  BUILDING: ['Foundation', 'Basement', 'Plinth Level', 'Columns', 'Slab Laid', 'Brick Work', 'Plastering', 'Painting', 'Finishings'],
};
// The work_stages master's DETAILED execution stages, keyed by work_type_id,
// loaded once from /work-stages/detailed-by-type. This is the SINGLE SOURCE for
// the In-Progress Sub-Stage flows — editing them on the /stages page flows
// straight through here. The IN_PROGRESS_STAGES_BY_GROUP constants above are only
// a fallback for legacy works with no work_type_id or while the master loads.
let masterDetailedByType: Record<string, string[]> = {};
// Gate sub-stages bucketed by abstract stage, also from the work_stages master
// (Not Started / In Progress / Completed / Held Up). Constants below are fallback.
let masterGates: Record<string, string[]> = {};
const setMasterStageOptions = (o: { gates?: Record<string, string[]>; detailedByType?: Record<string, string[]> }) => {
  masterGates = o?.gates || {};
  masterDetailedByType = o?.detailedByType || {};
};

// LSB is a bridge even though it sits under Cross Drainage; other CD works
// (culverts, causeways, road dams) use the culvert flow. Order matters.
const structureGroupForWorkType = (workType?: any, subType?: any): StructureGroup => {
  const t = String(workType ?? '').toLowerCase();
  const s = String(subType ?? '').toLowerCase();
  const hay = `${t} ${s}`;
  if (hay.includes('bridge') || hay.includes('lsb')) return 'BRIDGE';
  if (t.includes('building') || t.includes('repairs')) return 'BUILDING';
  if (t.includes('cross drainage') || hay.includes('culvert') || hay.includes('cause way') || hay.includes('causeway') || hay.includes('road dam')) return 'CULVERT';
  if (s.includes('cc road') || hay.includes('side drain') || t.includes('drain')) return 'CC_ROAD';
  return 'ROAD';
};
// Built-in gate fallbacks (used only if the work_stages master hasn't loaded).
const GATE_FALLBACK: Record<string, string[]> = {
  'Not Started': ['NS- Estimate Stage', 'NS- OAS Stage', 'NS- TS Stage', 'NS- Design Stage', 'NS- Tender Stage', 'NS- Agreement Stage', 'NS- Held Up'],
  'In Progress': ['IP- Work in Progress', 'IP- Held Up'],
  'Completed': ['Physically Completed (CR Not Approved)', 'CR Approved & Final Bill Pending', 'Final Bill Paid', 'Under DLP', 'Closed'],
  'Held Up': ['Held Up- Forest clearance', 'Held Up- Land Acquisition', 'Held Up- Land Dispute', 'Held Up- Court case', 'Held Up- V&E', 'Held Up- For want of RAS', 'Held Up- Contractors ill health'],
};
const gatesFor = (abstract: string): string[] => {
  const fromMaster = masterGates[abstract];
  return fromMaster && fromMaster.length ? fromMaster : (GATE_FALLBACK[abstract] || []);
};
const subStageOptionsForRow = (row: { abstract_stage_of_work?: any; work_type?: any; work_sub_type?: any; work_type_id?: any }): string[] => {
  const a = String(row.abstract_stage_of_work ?? '').trim();
  if (a === 'In Progress') {
    // Gate part (IP- …) + the structure-detailed part. Both come from the master;
    // detailed falls back to the built-in templates for legacy / unseeded types.
    const fromMaster = row.work_type_id != null ? masterDetailedByType[String(row.work_type_id)] : undefined;
    const detailed = (fromMaster && fromMaster.length)
      ? fromMaster
      : IN_PROGRESS_STAGES_BY_GROUP[structureGroupForWorkType(row.work_type, row.work_sub_type)];
    return [...gatesFor('In Progress'), ...detailed];
  }
  if (a === 'Not Started' || a === 'Completed' || a === 'Held Up') return gatesFor(a);
  return [];
};
// For the filter panel (not bound to a work type): for In Progress show the union
// of every structure group's detailed stages; otherwise the row-aware set.
const subStageFilterOptions = (stage: any): string[] => {
  if (String(stage ?? '').trim() === 'In Progress') {
    const all = [...gatesFor('In Progress'),
      ...Object.values(masterDetailedByType).flat(),
      ...Object.values(IN_PROGRESS_STAGES_BY_GROUP).flat()];
    return Array.from(new Set(all));
  }
  return subStageOptionsForRow({ abstract_stage_of_work: stage });
};

// Grant (col 3) — codes from the grants master.
const GRANT_CODES = ['CRR', 'MRR', 'SDF', 'FDR', 'PMGSY', 'MGNREGS', 'DMFT', 'GP_BLDG', 'IMS', 'RIDF', 'NABARD'];

// Category (col 37) — works_master.category_pst is enum('P','S','T').
const CATEGORY_PST = ['P', 'S', 'T'];

// Type of work (col 14) -> Sub Type (col 15) cascade. Categories and child lists
// match the official upload-format "Data Base" matrix exactly.
const WORK_TYPES_BY_CATEGORY: Record<string, string[]> = {
  Road: ['New BT Road', 'BT Renewals', 'BT Road', 'CC Road', 'Formation Road', 'Gravel Road'],
  'Cross Drainage work': ['LSB', 'Cause Way', 'Vented Cause way', 'Slab Culverts', 'Pipe Culverts', 'Road dams', 'Box Culverts'],
  Buildings: ['MPP Building', 'GP Buildings', 'IMS', 'School Building', 'Community Halls', 'Veterinary buildings', 'Agriculture Godown buildings', 'Others'],
  'Repairs to Buildings': ['MPP Building', 'GP Buildings', 'School Building', 'Class room', 'Community Halls', 'Veterinary buildings', 'Agriculture Godown buildings', 'Others'],
  Drain: [],
};
const WORK_CATEGORIES = Object.keys(WORK_TYPES_BY_CATEGORY);
const subTypeOptionsFor = (cat: any): string[] => WORK_TYPES_BY_CATEGORY[String(cat ?? '').trim()] || [];

// Column definitions in WW-Format order. `group` drives the colored band header.
const COLUMNS: ColumnDef[] = [
  { col: 2, key: 'oas_grant_level', label: 'OAS Grant Level', group: 'Identification', kind: 'select', options: GRANT_LEVELS, width: 120 },
  { col: 3, key: 'grant_type', label: 'Grant', group: 'Identification', kind: 'select', options: GRANT_CODES, width: 100 },

  { col: 4, key: 'territory_name', label: 'Territory', group: 'Location', kind: 'readonly', width: 110 },
  { col: 5, key: 'circle_name', label: 'Circle', group: 'Location', kind: 'readonly', width: 110 },
  { col: 6, key: 'district_name', label: 'District', group: 'Location', kind: 'readonly', width: 110 },
  { col: 7, key: 'division_name', label: 'Division', group: 'Location', kind: 'readonly', width: 110 },
  { col: 43, key: 'sub_division_name', label: 'Sub-division', group: 'Location', kind: 'readonly', width: 120 },
  { col: 8, key: 'mandal_name', label: 'Mandal', group: 'Location', kind: 'readonly', width: 120 },
  { col: 44, key: 'constituency_name', label: 'Constituency', group: 'Location', kind: 'readonly', width: 130 },

  { col: 9, key: 'work_name', label: 'Name of the work', group: 'Work', kind: 'text', width: 280 },

  { col: 10, key: 'oas_proceedings_number', label: 'OAS Proceedings / GO No.', group: 'OAS / RAS Sanction', kind: 'text', width: 150 },
  { col: 11, key: 'oas_sanction_date', label: 'OAS Sanction Date', group: 'OAS / RAS Sanction', kind: 'date', width: 140 },
  { col: 12, key: 'ras_proceedings_number', label: 'RAS GO No.', group: 'OAS / RAS Sanction', kind: 'text', width: 140 },
  { col: 13, key: 'ras_sanction_date', label: 'RAS Sanction Date', group: 'OAS / RAS Sanction', kind: 'date', width: 140 },

  { col: 14, key: 'work_type', label: 'Type of work', group: 'Work Details', kind: 'select', options: WORK_CATEGORIES, width: 140 },
  { col: 15, key: 'work_sub_type', label: 'Sub Type', group: 'Work Details', kind: 'depselect', dependsOn: 'work_type', depOptions: subTypeOptionsFor, width: 180 },
  { col: 16, key: 'length_sanctioned_km', label: 'Length sanctioned (KM)', group: 'Work Details', kind: 'num', width: 120 },
  { col: 17, key: 'estimate_cost', label: 'Estimate cost', group: 'Work Details', kind: 'num', width: 110 },

  { col: 18, key: 'agency_name', label: 'Name of Agency', group: 'Agreement', kind: 'text', width: 180 },
  { col: 19, key: 'tp', label: 'TP', group: 'Agreement', kind: 'text', width: 90 },
  { col: 20, key: 'agreement_date', label: 'Date of Agmt.', group: 'Agreement', kind: 'date', width: 140 },
  { col: 21, key: 'completion_date_set', label: 'Date set for completion', group: 'Agreement', kind: 'date', width: 150 },

  { col: 22, key: 'ras_amount', label: 'Revised Admn. Sanc. Amount', group: 'Revised Sanction', kind: 'num', width: 140 },
  { col: 23, key: 'ras_proceedings_number', label: 'RAS Proceedings / GO No.', group: 'Revised Sanction', kind: 'text', width: 150 },
  { col: 24, key: 'ras_sanction_date', label: 'RAS Sanction Date', group: 'Revised Sanction', kind: 'date', width: 140 },

  { col: 25, key: 'abstract_stage_of_work', label: 'Abstract Stage', group: 'Stage', kind: 'select', options: ABSTRACT_STAGES, width: 130 },
  { col: 26, key: 'sub_stage_of_work', label: 'Sub Stage', group: 'Stage', kind: 'depselect', dependsOn: 'abstract_stage_of_work', dependsOnKeys: ['abstract_stage_of_work', 'work_type', 'work_sub_type'], depOptionsRow: subStageOptionsForRow, width: 220 },

  { col: 27, key: 'fy_length_upto_last_fy', label: 'Total Length upto last FY', group: 'Physical Progress (Length KM)', kind: 'num', width: 120 },
  { col: 28, key: 'balance_length_spilled', label: 'Balance length Spilled over', group: 'Physical Progress (Length KM)', kind: 'formula', compute: fBalanceLength, width: 120 },
  { col: 29, key: 'fy_length_during', label: 'Length completed during FY', group: 'Physical Progress (Length KM)', kind: 'num', width: 120 },
  { col: 30, key: 'cumulative_length', label: 'Cumulative Length', group: 'Physical Progress (Length KM)', kind: 'formula', compute: fCumulativeLength, width: 120 },

  { col: 31, key: 'fy_exp_upto_last_fy', label: 'Total Exp upto last FY', group: 'Financial Progress (Expenditure)', kind: 'num', width: 120 },
  { col: 32, key: 'balance_cost_spilled', label: 'Balance Est. Cost Spilled over', group: 'Financial Progress (Expenditure)', kind: 'formula', compute: fBalanceCost, width: 120 },
  { col: 33, key: 'fy_exp_during', label: 'Exp during FY', group: 'Financial Progress (Expenditure)', kind: 'num', width: 120 },
  { col: 34, key: 'cumulative_expenditure', label: 'Cum. Exp on the work', group: 'Financial Progress (Expenditure)', kind: 'formula', compute: fCumulativeExp, width: 120 },

  { col: 35, key: 'bills_pending_pao_bro_loc', label: 'Bills pending with PAO', group: 'Bills', kind: 'num', width: 120 },
  { col: 36, key: 'work_done_not_recorded_bills', label: 'Work Done but not Recorded', group: 'Bills', kind: 'num', width: 130 },

  { col: 37, key: 'category_pst', label: 'Category (P/S/T)', group: 'Other', kind: 'select', options: CATEGORY_PST, width: 100 },
  { col: 38, key: 'detailed_stage_of_work', label: 'Detailed Stage / Remarks', group: 'Other', kind: 'text', width: 220 },

  // Read-only monitoring columns, derived server-side (mirrors dashboard EOAT/QC).
  { col: 39, key: 'is_eoat', label: 'EOAT', group: 'Monitoring', kind: 'derived', width: 110, render: fEoat },
  { col: 40, key: 'qc_status', label: 'QC Status', group: 'Monitoring', kind: 'derived', width: 130, render: fQcStatus },
  { col: 41, key: 'qc_wing', label: 'QC Wing', group: 'Monitoring', kind: 'derived', width: 90, render: fQcWing },
  { col: 42, key: 'qc_grade', label: 'QC Grade', group: 'Monitoring', kind: 'derived', width: 90, render: fQcGrade },
];

// Editable scalar fields sent on save (formula/readonly/name columns excluded).
const SAVE_KEYS: (keyof FullFormatRow)[] = [
  'sno', 'oas_grant_level', 'grant_type', 'work_name',
  'oas_proceedings_number', 'oas_sanction_date', 'ras_proceedings_number', 'ras_sanction_date',
  'work_type', 'work_sub_type', 'length_sanctioned_km', 'estimate_cost',
  'agency_name', 'tp', 'agreement_date', 'completion_date_set', 'ras_amount',
  'abstract_stage_of_work', 'sub_stage_of_work',
  'fy_length_upto_last_fy', 'fy_length_during', 'fy_exp_upto_last_fy', 'fy_exp_during',
  'bills_pending_pao_bro_loc', 'work_done_not_recorded_bills',
  'category_pst', 'detailed_stage_of_work', 'financial_year',
];

// Distinct colors per group band.
const GROUP_COLOR: Record<string, string> = {
  Identification: 'bg-slate-700',
  Location: 'bg-cyan-700',
  Work: 'bg-indigo-700',
  'OAS / RAS Sanction': 'bg-violet-700',
  'Work Details': 'bg-blue-700',
  Agreement: 'bg-teal-700',
  'Revised Sanction': 'bg-fuchsia-700',
  Stage: 'bg-amber-700',
  'Physical Progress (Length KM)': 'bg-orange-700',
  'Financial Progress (Expenditure)': 'bg-emerald-700',
  Bills: 'bg-rose-700',
  Other: 'bg-gray-700',
  Monitoring: 'bg-red-800',
};

// Merge consecutive same-group columns into header band cells.
const buildGroupBands = () => {
  const bands: { group: string; span: number }[] = [];
  COLUMNS.forEach((c) => {
    const last = bands[bands.length - 1];
    if (last && last.group === c.group) last.span += 1;
    else bands.push({ group: c.group, span: 1 });
  });
  return bands;
};

const toDateInput = (v: any): string => {
  if (!v) return '';
  const s = String(v);
  // Accept ISO datetime or yyyy-mm-dd; emit yyyy-mm-dd for the date input.
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
};

// ==========================================
// ROW (memoized so a keystroke only re-renders its own row)
// ==========================================
const GridRow = React.memo(function GridRow({
  row,
  index,
  onChange,
  onSave,
  onRevert,
  onDelete,
  onTimeline,
  districts = [],
  mandalOptions = [],
}: {
  row: EditableRow;
  index: number;
  onChange: (workId: number, key: string, value: string) => void;
  onSave: (workId: number) => void;
  onRevert: (workId: number) => void;
  onDelete: (workId: number) => void;
  onTimeline: (workId: number) => void;
  districts?: { id: number; name: string }[];
  mandalOptions?: { id: number; name: string }[];
}) {
  const isNew = (row as any)._isNew;
  return (
    <tr className={isNew ? 'bg-blue-50' : index % 2 ? 'bg-gray-50' : 'bg-white'}>
      <td className="sticky left-0 z-10 border px-2 py-1 text-center text-xs text-gray-500 bg-inherit">{isNew ? 'NEW' : index + 1}</td>
      {COLUMNS.map((c) => {
        const raw = (row as any)[c.key];
        // New rows: District & Mandal become editable pickers (they're FKs the
        // create endpoint needs; on existing rows these stay read-only names).
        if (isNew && c.key === 'district_name') {
          return (
            <td key={c.col} className="border px-1 py-0.5 bg-blue-50/40">
              <select className="w-full bg-white text-xs px-1 py-0.5 border border-blue-300 rounded" value={(row as any).district_id ?? ''} onChange={(e) => onChange(row.work_id, 'district_id', e.target.value)}>
                <option value="">District…</option>
                {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </td>
          );
        }
        if (isNew && c.key === 'mandal_name') {
          return (
            <td key={c.col} className="border px-1 py-0.5 bg-blue-50/40">
              <select className="w-full bg-white text-xs px-1 py-0.5 border border-blue-300 rounded" value={(row as any).mandal_id ?? ''} disabled={!(row as any).district_id} onChange={(e) => onChange(row.work_id, 'mandal_id', e.target.value)}>
                <option value="">{(row as any).district_id ? 'Mandal…' : 'District first'}</option>
                {mandalOptions.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </td>
          );
        }
        if (isNew && c.kind === 'readonly') {
          // Other hierarchy names are auto-derived on save — show a dash.
          return <td key={c.col} className="border px-2 py-1 text-xs text-gray-300 bg-gray-50">auto</td>;
        }
        if (c.kind === 'formula') {
          const val = c.compute!(row);
          return (
            <td key={c.col} className="border px-2 py-1 text-right text-xs bg-yellow-50 text-gray-700 font-medium" title="Auto-calculated">
              {val.toFixed(2)}
            </td>
          );
        }
        if (c.kind === 'readonly') {
          return (
            <td key={c.col} className="border px-2 py-1 text-xs text-gray-600 bg-gray-100">{raw ?? '-'}</td>
          );
        }
        if (c.kind === 'derived') {
          const d = c.render!(row);
          return (
            <td key={c.col} className={`border px-2 py-1 text-xs text-center bg-gray-50 ${d.cls ?? 'text-gray-600'}`} title="Auto-derived (read-only)">
              {d.text}
            </td>
          );
        }
        const common =
          'w-full bg-transparent text-xs px-1 py-0.5 border border-transparent rounded focus:border-blue-400 focus:bg-white outline-none';
        if (c.kind === 'select') {
          return (
            <td key={c.col} className="border px-1 py-0.5">
              <select className={common} value={raw ?? ''} onChange={(e) => onChange(row.work_id, String(c.key), e.target.value)}>
                <option value=""></option>
                {c.options!.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </td>
          );
        }
        if (c.kind === 'depselect') {
          // Options cascade from the parent field (c.dependsOn) of this row.
          const parentVal = (row as any)[c.dependsOn!];
          const parentLabel = COLUMNS.find((x) => x.key === c.dependsOn)?.label || 'parent';
          const opts = c.depOptionsRow ? c.depOptionsRow(row) : c.depOptions ? c.depOptions(parentVal) : [];
          const cur = (raw ?? '') as string;
          const noParent = !String(parentVal ?? '').trim();
          // Legacy/free-text value not in the cascade list for this parent.
          const invalid = !!cur && opts.length > 0 && !opts.includes(cur);
          return (
            <td key={c.col} className="border px-1 py-0.5">
              <select
                className={`${common} ${invalid ? 'border-red-400 bg-red-50 text-red-700' : ''}`}
                value={cur}
                disabled={noParent}
                title={
                  noParent
                    ? `Select ${parentLabel} first`
                    : invalid
                    ? `"${cur}" is not valid for ${parentLabel} "${parentVal}"`
                    : ''
                }
                onChange={(e) => onChange(row.work_id, String(c.key), e.target.value)}
              >
                <option value=""></option>
                {invalid && <option value={cur}>{cur} (current — invalid)</option>}
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </td>
          );
        }
        const type = c.kind === 'num' ? 'number' : c.kind === 'date' ? 'date' : 'text';
        const value = c.kind === 'date' ? toDateInput(raw) : (raw ?? '');
        return (
          <td key={c.col} className="border px-1 py-0.5">
            <input
              type={type}
              className={`${common} ${c.kind === 'num' ? 'text-right' : ''}`}
              value={value}
              step={c.kind === 'num' ? 'any' : undefined}
              onChange={(e) => onChange(row.work_id, String(c.key), e.target.value)}
            />
          </td>
        );
      })}
      <td className="sticky right-0 z-10 border px-2 py-1 text-center bg-inherit">
        <div className="flex items-center gap-1 justify-center">
          <button
            onClick={() => onSave(row.work_id)}
            disabled={row._saving || !row._dirty}
            title={row._error || (row._saved ? 'Saved' : 'Save row')}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${
              row._error
                ? 'bg-red-100 text-red-700'
                : row._saved
                ? 'bg-green-100 text-green-700'
                : row._dirty
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-default'
            }`}
          >
            {row._saving ? <RefreshCw className="h-3 w-3 animate-spin" /> : row._saved ? <Check className="h-3 w-3" /> : <Save className="h-3 w-3" />}
            {row._saving ? 'Saving' : row._saved ? 'Saved' : 'Save'}
          </button>
          <button
            onClick={() => onRevert(row.work_id)}
            disabled={row._saving || (!row._dirty && !isNew)}
            title={isNew ? 'Discard this new row' : 'Revert unsaved changes'}
            className={`p-1 rounded ${row._dirty || isNew ? 'text-amber-600 hover:bg-amber-50' : 'text-gray-300 cursor-default'}`}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDelete(row.work_id)}
            disabled={row._saving}
            title={isNew ? 'Remove this new row' : 'Delete work'}
            className="p-1 rounded text-red-500 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          {!isNew && (
            <button
              onClick={() => onTimeline(row.work_id)}
              title="Activity timeline (who changed what, when)"
              className="p-1 rounded text-blue-500 hover:bg-blue-50"
            >
              <Clock className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}, (prev, next) => prev.row === next.row && prev.index === next.index && prev.districts === next.districts && prev.mandalOptions === next.mandalOptions);

// ==========================================
// FILTERS (mirrors the dashboard filter set)
// ==========================================
interface Filters {
  search: string;
  grant: string;
  grantLevel: string;
  territoryId: string;
  circleId: string;
  divisionId: string;
  subDivisionId: string;
  mandalId: string;
  stage: string;
  subStage: string;
  category: string;
  workType: string;
  agencyName: string;
  minCost: string;
  maxCost: string;
  fromDate: string;
  toDate: string;
}
const EMPTY_FILTERS: Filters = {
  search: '', grant: '', grantLevel: '', territoryId: '', circleId: '', divisionId: '',
  subDivisionId: '', mandalId: '', stage: '', subStage: '', category: '', workType: '',
  agencyName: '', minCost: '', maxCost: '', fromDate: '', toDate: '',
};
// Strip empties so only active filters hit the API.
const cleanFilters = (f: Filters): Record<string, any> => {
  const out: Record<string, any> = { limit: 5000 };
  Object.entries(f).forEach(([k, v]) => { if (v !== '' && v != null) out[k] = v; });
  return out;
};

// Shared filter-control styling + labelled field wrapper.
const SEL = 'w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white disabled:bg-gray-100 disabled:text-gray-400';
const FField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="flex flex-col gap-0.5">
    <span className="text-[11px] font-medium text-gray-500">{label}</span>
    {children}
  </label>
);

// ==========================================
// IMPORT DRY-RUN PREVIEW MODAL
// ------------------------------------------
// Shows what a dry run found and gates the actual DB write behind a confirm.
// When every row is valid it's a clean import; when some rows are invalid the
// confirm imports the valid rows and skips the invalid ones (spelled out).
// ==========================================
const ImportPreviewModal: React.FC<{
  preview: ImportPreview;
  fileName?: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ preview, fileName, busy, onCancel, onConfirm }) => {
  const s = preview.summary;
  const invalidRows = preview.invalid ?? [];
  const allValid = s.invalid === 0 && s.valid > 0;
  const nothingToImport = s.valid === 0;

  const Stat = ({ label, value, color }: { label: string; value: number; color: string }) => (
    <div className={`flex-1 rounded-md border px-3 py-2 text-center ${color}`}>
      <div className="text-lg font-bold leading-none">{value}</div>
      <div className="text-[11px] mt-1">{label}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h2 className="text-base font-bold text-gray-800">Import preview — dry run</h2>
            {fileName && <p className="text-xs text-gray-500 truncate max-w-md">{fileName}</p>}
          </div>
          <button onClick={onCancel} disabled={busy} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-4 py-3 overflow-y-auto">
          <p className="text-xs text-gray-500 mb-2">
            No records have been written yet. Review the breakdown below, then confirm to import.
          </p>
          <div className="flex gap-2 mb-3">
            <Stat label="Total rows" value={s.total} color="bg-gray-50 border-gray-200 text-gray-700" />
            <Stat label="Valid" value={s.valid} color="bg-green-50 border-green-200 text-green-700" />
            <Stat label="Invalid" value={s.invalid} color="bg-red-50 border-red-200 text-red-700" />
            <Stat label="Duplicates" value={s.duplicates} color="bg-amber-50 border-amber-200 text-amber-700" />
          </div>

          {allValid && (
            <div className="flex items-center gap-2 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
              <Check className="h-4 w-4 shrink-0" /> All {s.valid} row(s) passed validation and are ready to import.
            </div>
          )}

          {s.invalid > 0 && (
            <div className="mt-2">
              <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 mb-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {nothingToImport
                  ? `No rows can be imported — all ${s.invalid} row(s) have errors.`
                  : `${s.invalid} row(s) have errors and will be SKIPPED. Only the ${s.valid} valid row(s) will be imported.`}
              </div>
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="text-left px-3 py-1.5 w-20">Excel row</th>
                      <th className="text-left px-3 py-1.5">Error(s)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {invalidRows.map((r) => (
                      <tr key={r.row}>
                        <td className="px-3 py-1.5 font-medium text-gray-700 align-top">{r.row}</td>
                        <td className="px-3 py-1.5 text-red-700">{(r.errors || []).join('; ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
          <button onClick={onCancel} disabled={busy} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || nothingToImport}
            className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm text-white disabled:opacity-50 ${allValid ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}`}
          >
            <Check className={`h-4 w-4 ${busy ? 'animate-pulse' : ''}`} />
            {busy
              ? 'Importing…'
              : nothingToImport
              ? 'Nothing to import'
              : allValid
              ? `Confirm & import ${s.valid} work(s)`
              : `Import ${s.valid} valid, skip ${s.invalid}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// MAIN
// ==========================================
const WorksFullFormat: React.FC = () => {
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [timelineWorkId, setTimelineWorkId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // `filters` is the form draft; `applied` is what's actually loaded.
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);

  // Hierarchy option lists for the cascading dropdowns.
  const [territories, setTerritories] = useState<any[]>([]);
  const [circles, setCircles] = useState<any[]>([]);
  const [divisions, setDivisions] = useState<any[]>([]);
  const [subDivisions, setSubDivisions] = useState<any[]>([]);
  const [mandals, setMandals] = useState<any[]>([]);

  // Add-new-row + upload state.
  const [allDistricts, setAllDistricts] = useState<{ id: number; name: string }[]>([]);
  const [mandalsByDist, setMandalsByDist] = useState<Record<string, { id: number; name: string }[]>>({});
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // Dry-run preview state: the validation result + the file held back for the
  // commit step, so Upload -> dry run -> confirm -> write to DB.
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const tempIdRef = useRef(-1);
  // Pristine copy of each loaded row, for Revert.
  const originalById = useRef<Record<number, EditableRow>>({});

  const bands = useMemo(buildGroupBands, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Load the work_stages master once (single source for the Sub-Stage
      // dropdowns — gate stages + detailed execution stages) BEFORE rows, so
      // options render correctly on first paint. Retries if it was empty/failed.
      if (Object.keys(masterGates).length === 0 && Object.keys(masterDetailedByType).length === 0) {
        setMasterStageOptions(await worksApiService.getFullFormatStageOptions());
      }
      const data = await worksApiService.getWorksFullFormat(cleanFilters(applied));
      originalById.current = {};
      (data as EditableRow[]).forEach((r) => { originalById.current[r.work_id] = { ...r }; });
      setRows(data as EditableRow[]);
    } catch (e: any) {
      setError(e?.message || 'Failed to load works');
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => { load(); }, [load]);

  // Top-level lists once; divisions carry circle_id so we filter them client-side.
  useEffect(() => {
    fetchHierarchy('/ce-territories/active').then(setTerritories);
    fetchHierarchy('/divisions?limit=1000').then(setDivisions);
  }, []);
  // Cascades: circles by territory, sub-divisions by division, mandals by sub-division.
  useEffect(() => {
    if (filters.territoryId) fetchHierarchy(`/circles/ce-territory/${filters.territoryId}`).then(setCircles);
    else setCircles([]);
  }, [filters.territoryId]);
  useEffect(() => {
    if (filters.divisionId) fetchHierarchy(`/sub-divisions/division/${filters.divisionId}`).then(setSubDivisions);
    else setSubDivisions([]);
  }, [filters.divisionId]);
  useEffect(() => {
    if (filters.subDivisionId) fetchHierarchy(`/mandals/sub-division/${filters.subDivisionId}`).then(setMandals);
    else setMandals([]);
  }, [filters.subDivisionId]);

  const divisionOptions = useMemo(
    () => (filters.circleId ? divisions.filter((d) => Number(d.circle_id) === Number(filters.circleId)) : divisions),
    [divisions, filters.circleId],
  );

  // Districts list (for new-row District picker), loaded once.
  useEffect(() => {
    fetchHierarchy('/master-data/districts/stats').then((list) =>
      setAllDistricts(list.map((d: any) => ({ id: d.district_id || d.id, name: d.district_name || d.name })).filter((d: any) => d.id && d.name)),
    );
  }, []);
  const loadMandalsForDistrict = useCallback(async (districtId: string) => {
    if (!districtId || mandalsByDist[districtId]) return;
    const list = await fetchHierarchy(`/master-data/districts/${districtId}/mandals`);
    setMandalsByDist((prev) => ({ ...prev, [districtId]: list.map((m: any) => ({ id: m.mandal_id || m.id, name: m.mandal_name || m.name })).filter((m: any) => m.id && m.name) }));
  }, [mandalsByDist]);

  // Prepend a blank, editable new-work row.
  const addNewRow = () => {
    const id = tempIdRef.current--;
    const blank: any = { work_id: id, _isNew: true, _dirty: true, sno: null, oas_grant_level: 'State', abstract_stage_of_work: 'Not Started' };
    setRows((prev) => [blank, ...prev]);
  };

  // Bulk upload: step 1 is always a dry run. Picking a file validates it
  // server-side (no writes) and opens the preview; the actual DB write only
  // happens when the user confirms in the modal (doCommit).
  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) { void doValidate(file); }
    if (fileRef.current) fileRef.current.value = '';
  };
  const doValidate = async (file: File) => {
    try {
      setImporting(true); setImportMsg(null); setPreview(null); setPendingFile(null);
      const res = await worksApiService.validateWorksExcel(file);
      setPreview(res);
      setPendingFile(file);
    } catch (err: any) {
      setImportMsg({ ok: false, text: err?.message || 'Validation failed' });
    } finally { setImporting(false); }
  };
  const cancelPreview = () => { setPreview(null); setPendingFile(null); };
  const doCommit = async () => {
    if (!pendingFile) return;
    try {
      setImporting(true);
      const res = await worksApiService.commitWorksExcel(pendingFile);
      const s = res?.summary || {};
      const imported = s.imported ?? 0;
      const skipped = s.skipped ?? 0;
      const failed = s.failed ?? 0;
      const parts = [`Imported ${imported}`];
      if (failed) parts.push(`failed ${failed}`);
      if (skipped) parts.push(`skipped ${skipped} invalid`);
      if (s.total != null) parts.push(`of ${s.total}`);
      setImportMsg({ ok: true, text: `${parts.join(', ')}.` });
      cancelPreview();
      await load();
    } catch (err: any) {
      setImportMsg({ ok: false, text: err?.message || 'Import failed' });
    } finally { setImporting(false); }
  };

  // Set a filter; clear dependent children when a parent hierarchy level changes.
  const setF = (patch: Partial<Filters>) => setFilters((p) => ({ ...p, ...patch }));
  const applyFilters = () => setApplied(filters);
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); };
  const activeCount = Object.values(applied).filter((v) => v !== '').length;

  const handleChange = useCallback((workId: number, key: string, value: string) => {
    if (key === 'district_id') { void loadMandalsForDistrict(value); }
    setRows((prev) => prev.map((r) => {
      if (r.work_id !== workId) return r;
      const next: EditableRow = { ...r, [key]: value, _dirty: true, _saved: false, _error: null };
      // Picking a new district clears the chosen mandal.
      if (key === 'district_id') (next as any).mandal_id = '';
      // Trigger: editing a parent field clears any dependent dropdown whose
      // current value is no longer valid (Abstract Stage / Type of work -> Sub
      // Stage, Type of work -> Sub Type). Row-aware columns (depOptionsRow) list
      // every triggering field in dependsOnKeys.
      COLUMNS.forEach((c) => {
        if (c.kind !== 'depselect') return;
        const triggers = c.dependsOnKeys || (c.dependsOn ? [c.dependsOn] : []);
        if (!triggers.includes(key)) return;
        const allowed = c.depOptionsRow ? c.depOptionsRow(next) : c.depOptions ? c.depOptions(value) : [];
        const cur = String((r as any)[c.key] ?? '');
        if (cur && allowed.length > 0 && !allowed.includes(cur)) {
          (next as any)[c.key] = '';
        }
      });
      return next;
    }));
  }, [loadMandalsForDistrict]);

  const handleSave = useCallback(async (workId: number) => {
    let target: EditableRow | undefined;
    setRows((prev) => prev.map((r) => {
      if (r.work_id !== workId) return r;
      target = r;
      return { ...r, _saving: true, _error: null };
    }));
    if (!target) return;
    const t: any = target;
    const payload: Record<string, any> = {};
    SAVE_KEYS.forEach((k) => { payload[k] = (target as any)[k]; });
    try {
      if (t._isNew) {
        // Create: needs District + Mandal (FKs) the create endpoint resolves from.
        if (!t.district_id || !t.mandal_id) throw new Error('Select District and Mandal');
        if (!t.work_name) throw new Error('Work Name is required');
        const created = await worksApiService.createWorkFullFormat({ ...payload, district_id: Number(t.district_id), mandal_id: Number(t.mandal_id), work_type: t.work_type, work_sub_type: t.work_sub_type });
        setImportMsg({ ok: true, text: `Work created (ID ${created.work_id}).` });
        await load();
      } else {
        await worksApiService.saveWorkFullFormat(workId, payload);
        setRows((prev) => prev.map((r) => (r.work_id === workId ? { ...r, _saving: false, _dirty: false, _saved: true, _error: null } : r)));
      }
    } catch (e: any) {
      setRows((prev) => prev.map((r) => (r.work_id === workId ? { ...r, _saving: false, _error: e?.message || 'Save failed' } : r)));
    }
  }, [load]);

  // Revert: drop a new row, or restore an edited row to its loaded values.
  const handleRevert = useCallback((workId: number) => {
    setRows((prev) => {
      if (workId < 0) return prev.filter((r) => r.work_id !== workId);
      const orig = originalById.current[workId];
      return orig ? prev.map((r) => (r.work_id === workId ? { ...orig } : r)) : prev;
    });
  }, []);

  // Delete: remove a new row locally, or soft-delete an existing work via API.
  const handleDelete = useCallback(async (workId: number) => {
    if (workId < 0) { setRows((prev) => prev.filter((r) => r.work_id !== workId)); return; }
    if (!window.confirm(`Delete work #${workId}? It will be removed from the active list (soft-deleted).`)) return;
    setRows((prev) => prev.map((r) => (r.work_id === workId ? { ...r, _saving: true, _error: null } : r)));
    try {
      await worksApiService.deleteWork(workId);
      setRows((prev) => prev.filter((r) => r.work_id !== workId));
      setImportMsg({ ok: true, text: `Work #${workId} deleted.` });
    } catch (e: any) {
      setRows((prev) => prev.map((r) => (r.work_id === workId ? { ...r, _saving: false, _error: e?.message || 'Delete failed' } : r)));
    }
  }, []);

  const dirtyCount = rows.filter((r) => r._dirty).length;

  return (
    <div className="p-3 md:p-4 bg-gray-50 min-h-screen">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h1 className="text-lg font-bold text-gray-800">Works &mdash; Full Format (38 columns)</h1>
          <p className="text-xs text-gray-500">
            Editable grid mirroring the upload template. <span className="bg-yellow-50 px-1">Yellow</span> columns are auto-calculated; grey columns (hierarchy) are read-only.
            {dirtyCount > 0 && <span className="ml-2 text-blue-600 font-medium">{dirtyCount} unsaved row(s)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1.5 h-4 w-4 text-gray-400" />
            <input
              value={filters.search}
              onChange={(e) => setF({ search: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              placeholder="Search work / agency / GO no…"
              className="border border-gray-300 rounded-md pl-8 pr-3 py-1.5 text-sm bg-white w-56"
            />
          </div>
          <button
            onClick={() => setShowFilters((s) => !s)}
            className={`flex items-center gap-1 border rounded-md px-3 py-1.5 text-sm ${activeCount > 0 ? 'border-blue-500 text-blue-700 bg-blue-50' : 'border-gray-300 bg-white hover:bg-gray-50'}`}
          >
            <Filter className="h-4 w-4" /> Filters{activeCount > 0 ? ` (${activeCount})` : ''}
          </button>
          <button onClick={applyFilters} className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white hover:bg-gray-50">Apply</button>
          <button onClick={load} className="flex items-center gap-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white hover:bg-gray-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={addNewRow} className="flex items-center gap-1 rounded-md px-3 py-1.5 text-sm bg-green-600 text-white hover:bg-green-700">
            <Plus className="h-4 w-4" /> Add New Work
          </button>
          <a href={`${API_BASE}/works-excel-import/template`} className="flex items-center gap-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white hover:bg-gray-50" title="Download import template">
            <FileDown className="h-4 w-4" /> Template
          </a>
          <button onClick={() => fileRef.current?.click()} disabled={importing} title="Validate an Excel file (dry run), then confirm to import" className="flex items-center gap-1 rounded-md px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
            <Upload className={`h-4 w-4 ${importing ? 'animate-pulse' : ''}`} /> {importing && !preview ? 'Checking…' : 'Upload'}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportFile} />
        </div>
      </div>

      {importMsg && (
        <div className={`mb-3 rounded-md px-3 py-2 text-sm flex items-center justify-between ${importMsg.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          <span>{importMsg.text}</span>
          <button onClick={() => setImportMsg(null)}><X className="h-4 w-4" /></button>
        </div>
      )}

      {preview && (
        <ImportPreviewModal
          preview={preview}
          fileName={pendingFile?.name}
          busy={importing}
          onCancel={cancelPreview}
          onConfirm={doCommit}
        />
      )}

      {showFilters && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 mb-3">
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-x-3 gap-y-2">
            {/* Hierarchy cascade */}
            <FField label="Territory">
              <select className={SEL} value={filters.territoryId}
                onChange={(e) => setF({ territoryId: e.target.value, circleId: '', divisionId: '', subDivisionId: '', mandalId: '' })}>
                <option value="">All Territories</option>
                {territories.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </FField>
            <FField label="Circle">
              <select className={SEL} value={filters.circleId} disabled={!filters.territoryId}
                onChange={(e) => setF({ circleId: e.target.value, divisionId: '', subDivisionId: '', mandalId: '' })}>
                <option value="">{filters.territoryId ? 'All Circles' : 'Select Territory'}</option>
                {circles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </FField>
            <FField label="Division">
              <select className={SEL} value={filters.divisionId}
                onChange={(e) => setF({ divisionId: e.target.value, subDivisionId: '', mandalId: '' })}>
                <option value="">All Divisions</option>
                {divisionOptions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </FField>
            <FField label="Sub-division">
              <select className={SEL} value={filters.subDivisionId} disabled={!filters.divisionId}
                onChange={(e) => setF({ subDivisionId: e.target.value, mandalId: '' })}>
                <option value="">{filters.divisionId ? 'All Sub-divisions' : 'Select Division'}</option>
                {subDivisions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </FField>
            <FField label="Mandal">
              <select className={SEL} value={filters.mandalId} disabled={!filters.subDivisionId}
                onChange={(e) => setF({ mandalId: e.target.value })}>
                <option value="">{filters.subDivisionId ? 'All Mandals' : 'Select Sub-division'}</option>
                {mandals.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </FField>
            {/* Grant / classification */}
            <FField label="Grant">
              <select className={SEL} value={filters.grant} onChange={(e) => setF({ grant: e.target.value })}>
                <option value="">All Grants</option>
                {GRANT_CODES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </FField>
            <FField label="Grant Level">
              <select className={SEL} value={filters.grantLevel} onChange={(e) => setF({ grantLevel: e.target.value })}>
                <option value="">All</option>
                {GRANT_LEVELS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </FField>
            <FField label="Work Type">
              <select className={SEL} value={filters.workType} onChange={(e) => setF({ workType: e.target.value })}>
                <option value="">All Types</option>
                {WORK_CATEGORIES.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </FField>
            <FField label="Category">
              <select className={SEL} value={filters.category} onChange={(e) => setF({ category: e.target.value })}>
                <option value="">All</option>
                {CATEGORY_PST.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FField>
            {/* Stage cascade */}
            <FField label="Abstract Stage">
              <select className={SEL} value={filters.stage} onChange={(e) => setF({ stage: e.target.value, subStage: '' })}>
                <option value="">All Stages</option>
                {ABSTRACT_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </FField>
            <FField label="Sub Stage">
              <select className={SEL} value={filters.subStage} disabled={!filters.stage}
                onChange={(e) => setF({ subStage: e.target.value })}>
                <option value="">{filters.stage ? 'All Sub Stages' : 'Select Stage'}</option>
                {subStageFilterOptions(filters.stage).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </FField>
            <FField label="Agency">
              <input className={SEL} value={filters.agencyName} placeholder="contains…"
                onChange={(e) => setF({ agencyName: e.target.value })} />
            </FField>
            {/* Cost & date ranges */}
            <FField label="Min Cost">
              <input type="number" className={SEL} value={filters.minCost} onChange={(e) => setF({ minCost: e.target.value })} />
            </FField>
            <FField label="Max Cost">
              <input type="number" className={SEL} value={filters.maxCost} onChange={(e) => setF({ maxCost: e.target.value })} />
            </FField>
            <FField label="OAS Date From">
              <input type="date" className={SEL} value={filters.fromDate} onChange={(e) => setF({ fromDate: e.target.value })} />
            </FField>
            <FField label="OAS Date To">
              <input type="date" className={SEL} value={filters.toDate} onChange={(e) => setF({ toDate: e.target.value })} />
            </FField>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={applyFilters} className="flex items-center gap-1 bg-blue-600 text-white rounded-md px-4 py-1.5 text-sm hover:bg-blue-700">
              <Filter className="h-4 w-4" /> Apply Filters
            </button>
            <button onClick={clearFilters} className="flex items-center gap-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white hover:bg-gray-50">
              <X className="h-4 w-4" /> Clear
            </button>
            <span className="text-xs text-gray-500">{rows.length} works loaded{activeCount > 0 ? ` · ${activeCount} filter(s) active` : ''}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 flex items-center justify-between">
          <div className="flex items-center"><AlertTriangle className="h-5 w-5 text-red-400 mr-2" /><span className="text-red-800 text-sm">{error}</span></div>
          <button onClick={load} className="text-red-600 hover:text-red-800 underline text-sm">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><RefreshCw className="h-8 w-8 animate-spin text-blue-500 mr-3" /><span className="text-gray-600">Loading works…</span></div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-auto" style={{ maxHeight: 'calc(100vh - 130px)' }}>
          <table className="border-collapse text-xs">
            <thead className="sticky top-0 z-20">
              {/* Group band row */}
              <tr>
                <th className="sticky left-0 z-30 bg-gray-800 text-white border px-2 py-1.5 w-10">#</th>
                {bands.map((b, i) => (
                  <th key={i} colSpan={b.span} className={`${GROUP_COLOR[b.group] || 'bg-gray-700'} text-white border px-2 py-1.5 text-center font-semibold whitespace-nowrap`}>
                    {b.group}
                  </th>
                ))}
                <th className="sticky right-0 z-30 bg-gray-800 text-white border px-2 py-1.5">Action</th>
              </tr>
              {/* Column label row */}
              <tr className="bg-gray-100">
                <th className="sticky left-0 z-30 bg-gray-100 border px-2 py-1.5 text-gray-500">No</th>
                {COLUMNS.map((c) => (
                  <th key={c.col} style={{ minWidth: c.width }} className="border px-2 py-1.5 text-left font-medium text-gray-600 whitespace-nowrap">
                    {c.col <= 42 && <span className="text-gray-400 mr-1">{c.col}.</span>}{c.label}
                  </th>
                ))}
                <th className="sticky right-0 z-30 bg-gray-100 border px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <GridRow key={r.work_id} row={r} index={i} onChange={handleChange} onSave={handleSave}
                  onRevert={handleRevert} onDelete={handleDelete} onTimeline={setTimelineWorkId}
                  districts={(r as any)._isNew ? allDistricts : undefined}
                  mandalOptions={(r as any)._isNew ? (mandalsByDist[(r as any).district_id] || []) : undefined} />
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={COLUMNS.length + 2} className="px-4 py-10 text-center text-gray-400">No works found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <WorkTimelineModal workId={timelineWorkId} onClose={() => setTimelineWorkId(null)} />
    </div>
  );
};

export default WorksFullFormat;
